import React, { useState, useEffect } from "react";

import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";

import {enUS, ptBR, esES} from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";
import { SocketContext, SocketManager } from './context/Socket/SocketContext';

import Routes from "./routes";

const queryClient = new QueryClient();

// ============================================
// 🎨 OMNIACTION - TEMA MODERNO
// ============================================

// Paleta de cores principal
const colors = {
    // Cores primárias - Índigo vibrante
    primary: {
        main: "#6366F1",      // Índigo principal
        light: "#818CF8",     // Índigo claro
        dark: "#4F46E5",      // Índigo escuro
        contrast: "#FFFFFF"
    },
    // Cores secundárias - Teal/Verde água
    secondary: {
        main: "#14B8A6",      // Teal
        light: "#2DD4BF",     // Teal claro
        dark: "#0D9488",      // Teal escuro
        contrast: "#FFFFFF"
    },
    // Cores de sucesso
    success: {
        main: "#22C55E",      // Verde
        light: "#4ADE80",
        dark: "#16A34A",
        gradient: "linear-gradient(135deg, #22C55E 0%, #16A34A 100%)"
    },
    // Cores de alerta
    warning: {
        main: "#F59E0B",      // Âmbar
        light: "#FBBF24",
        dark: "#D97706",
        gradient: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
    },
    // Cores de erro
    error: {
        main: "#EF4444",      // Vermelho
        light: "#F87171",
        dark: "#DC2626",
        gradient: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)"
    },
    // Cores de info
    info: {
        main: "#3B82F6",      // Azul
        light: "#60A5FA",
        dark: "#2563EB",
        gradient: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)"
    },
    // Gradientes para cards do dashboard
    gradients: {
        primary: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
        secondary: "linear-gradient(135deg, #14B8A6 0%, #06B6D4 100%)",
        success: "linear-gradient(135deg, #22C55E 0%, #10B981 100%)",
        warning: "linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)",
        info: "linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)",
        dark: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
        purple: "linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%)",
        pink: "linear-gradient(135deg, #EC4899 0%, #F43F5E 100%)",
        cyan: "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
    }
};

const App = () => {
    const [locale, setLocale] = useState();

    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
    const preferredTheme = window.localStorage.getItem("preferredTheme");
    const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");

    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
            },
        }),
        []
    );

    const theme = createTheme(
        {
            // Tipografia moderna
            typography: {
                fontFamily: '"Inter", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
                h1: { fontWeight: 700, letterSpacing: '-0.025em' },
                h2: { fontWeight: 700, letterSpacing: '-0.025em' },
                h3: { fontWeight: 600, letterSpacing: '-0.025em' },
                h4: { fontWeight: 600 },
                h5: { fontWeight: 600 },
                h6: { fontWeight: 600 },
                button: { textTransform: 'none', fontWeight: 600 },
            },
            // Bordas arredondadas modernas
            shape: {
                borderRadius: 12,
            },
            // Sombras suaves
            shadows: [
                'none',
                '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
                '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
                '0 25px 50px -12px rgb(0 0 0 / 0.25)',
            ],
            // Estilos de scrollbar
            scrollbarStyles: {
                "&::-webkit-scrollbar": {
                    width: '6px',
                    height: '6px',
                },
                "&::-webkit-scrollbar-track": {
                    background: mode === "light" ? "#F1F5F9" : "#1E293B",
                    borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: mode === "light" ? "#CBD5E1" : "#475569",
                    borderRadius: "3px",
                    "&:hover": {
                        backgroundColor: mode === "light" ? "#94A3B8" : "#64748B",
                    }
                },
            },
            scrollbarStylesSoft: {
                "&::-webkit-scrollbar": {
                    width: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: mode === "light" ? "#E2E8F0" : "#334155",
                    borderRadius: "3px",
                },
            },
            // Paleta de cores
            palette: {
                type: mode,
                // Cores principais
                primary: { 
                    main: colors.primary.main,
                    light: colors.primary.light,
                    dark: colors.primary.dark,
                    contrastText: colors.primary.contrast
                },
                secondary: { 
                    main: colors.secondary.main,
                    light: colors.secondary.light,
                    dark: colors.secondary.dark,
                    contrastText: colors.secondary.contrast
                },
                success: { main: colors.success.main },
                warning: { main: colors.warning.main },
                error: { main: colors.error.main },
                info: { main: colors.info.main },
                // Cores de texto
                textPrimary: mode === "light" ? "#1E293B" : "#F1F5F9",
                textSecondary: mode === "light" ? "#64748B" : "#94A3B8",
                borderPrimary: mode === "light" ? "#E2E8F0" : "#334155",
                // Cores de fundo
                background: {
                    default: mode === "light" ? "#F8FAFC" : "#0F172A",
                    paper: mode === "light" ? "#FFFFFF" : "#1E293B",
                },
                // Cores customizadas para o sistema
                dark: { main: mode === "light" ? "#1E293B" : "#F1F5F9" },
                light: { main: mode === "light" ? "#F1F5F9" : "#1E293B" },
                tabHeaderBackground: mode === "light" ? "#F1F5F9" : "#1E293B",
                optionsBackground: mode === "light" ? "#FFFFFF" : "#1E293B",
                options: mode === "light" ? "#F8FAFC" : "#334155",
                fontecor: mode === "light" ? colors.primary.main : colors.primary.light,
                fancyBackground: mode === "light" ? "#F8FAFC" : "#0F172A",
                bordabox: mode === "light" ? "#E2E8F0" : "#334155",
                newmessagebox: mode === "light" ? "#EFF6FF" : "#1E3A5F",
                inputdigita: mode === "light" ? "#FFFFFF" : "#1E293B",
                contactdrawer: mode === "light" ? "#FFFFFF" : "#1E293B",
                announcements: mode === "light" ? "#F1F5F9" : "#1E293B",
                login: mode === "light" ? "#FFFFFF" : "#1E293B",
                announcementspopover: mode === "light" ? "#FFFFFF" : "#1E293B",
                chatlist: mode === "light" ? "#F8FAFC" : "#1E293B",
                boxlist: mode === "light" ? "#F1F5F9" : "#334155",
                boxchatlist: mode === "light" ? "#F1F5F9" : "#1E293B",
                total: mode === "light" ? "#FFFFFF" : "#1E293B",
                messageIcons: mode === "light" ? "#64748B" : "#94A3B8",
                inputBackground: mode === "light" ? "#FFFFFF" : "#1E293B",
                // Gradiente da barra superior
                barraSuperior: mode === "light" 
                    ? colors.gradients.primary
                    : "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
                boxticket: mode === "light" ? "#F1F5F9" : "#334155",
                campaigntab: mode === "light" ? "#F1F5F9" : "#334155",
                mediainput: mode === "light" ? "#F1F5F9" : "#1E293B",
                // Cores para cards do dashboard (gradientes)
                cardPrimary: colors.gradients.primary,
                cardSecondary: colors.gradients.secondary,
                cardSuccess: colors.gradients.success,
                cardWarning: colors.gradients.warning,
                cardInfo: colors.gradients.info,
                cardPurple: colors.gradients.purple,
                cardPink: colors.gradients.pink,
                cardCyan: colors.gradients.cyan,
                // Cores sólidas dos gradientes (para referência)
                colors: colors,
            },
            // Override de componentes
            overrides: {
                MuiButton: {
                    root: {
                        borderRadius: 8,
                        padding: '8px 16px',
                        fontWeight: 600,
                    },
                    contained: {
                        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
                        '&:hover': {
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                        }
                    },
                    containedPrimary: {
                        background: colors.gradients.primary,
                        '&:hover': {
                            background: colors.primary.dark,
                        }
                    }
                },
                MuiPaper: {
                    root: {
                        backgroundImage: 'none',
                    },
                    rounded: {
                        borderRadius: 12,
                    },
                    elevation1: {
                        boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
                    },
                    elevation2: {
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                    },
                    elevation3: {
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
                    },
                },
                MuiCard: {
                    root: {
                        borderRadius: 16,
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                    }
                },
                MuiTextField: {
                    root: {
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 8,
                        }
                    }
                },
                MuiOutlinedInput: {
                    root: {
                        borderRadius: 8,
                    }
                },
                MuiChip: {
                    root: {
                        borderRadius: 8,
                        fontWeight: 500,
                    }
                },
                MuiAvatar: {
                    root: {
                        fontWeight: 600,
                    }
                },
                MuiListItem: {
                    root: {
                        borderRadius: 8,
                        marginBottom: 2,
                    }
                },
                MuiTab: {
                    root: {
                        textTransform: 'none',
                        fontWeight: 600,
                        minWidth: 'auto',
                    }
                },
                MuiDialog: {
                    paper: {
                        borderRadius: 16,
                    }
                },
                MuiTooltip: {
                    tooltip: {
                        borderRadius: 8,
                        fontSize: '0.75rem',
                    }
                },
                MuiDrawer: {
                    paper: {
                        borderRight: 'none',
                    }
                }
            },
            mode,
        },
        locale
    );

    useEffect(() => {
        const i18nlocale = localStorage.getItem("i18nextLng");
        const browserLocale = i18nlocale?.substring(0, 2) ?? 'pt';

        if (browserLocale === "pt"){
            setLocale(ptBR);
        }else if( browserLocale === "en" ) {
            setLocale(enUS)
        }else if( browserLocale === "es" )
            setLocale(esES)

    }, []);

    useEffect(() => {
        window.localStorage.setItem("preferredTheme", mode);
    }, [mode]);



    return (
        <ColorModeContext.Provider value={{ colorMode }}>
            <ThemeProvider theme={theme}>
                <QueryClientProvider client={queryClient}>
                  <SocketContext.Provider value={SocketManager}>
                      <Routes />
                  </SocketContext.Provider>
                </QueryClientProvider>
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};

export default App;
